import attendanceService from 'ApiService/Student/attendanceService';
import MiniCalendar from 'components/calendar/MiniCalendar';
import Card from 'components/card';
import { AuthContext } from 'contexts/AuthContext';
import Nodatafound from 'pages/Common/Nodatafound';
import { useContext, useEffect, useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { MdOutlineTopic, MdOutlineLibraryBooks, MdCheckCircle, MdCancel, MdSchool, MdAssignment } from 'react-icons/md';
import { toLocalDateStr } from 'utils/date';

const StudentAttendanceReport = () => {
  const [monthlyData, setMonthlyData] = useState({});
  const [batchInfo, setBatchInfo] = useState(null);
  const [courseInfo, setCourseInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const { state } = useContext(AuthContext);
  const [date, setDate] = useState(new Date());
  const [activeStartDate, setActiveStartDate] = useState(new Date());

  const fetchMonthlyData = async (targetDate) => {
    setLoading(true);
    try {
      const month = targetDate.getMonth() + 1;
      const year = targetDate.getFullYear();
      const response = await attendanceService.getMonthlyAttendance(month, year);

      if (response.data.success) {
        setMonthlyData(response.data.attendanceMap || {});
        setBatchInfo(response.data.batch || null);
        setCourseInfo(response.data.course || null);
      } else {
        setMonthlyData({});
      }
    } catch (error) {
      console.error(error);
      setMonthlyData({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMonthlyData(activeStartDate);
  }, [activeStartDate]);

  const handleDateChange = (dateObj) => {
    setDate(dateObj);
  };

  const onActiveStartDateChange = ({ activeStartDate: newActiveStartDate }) => {
    setActiveStartDate(newActiveStartDate);
  };

  const completionDate = useMemo(() => {
    if (!courseInfo || !batchInfo?.completedCourses) return null;
    const completion = batchInfo.completedCourses.find(
      (cc) => cc.course.toString() === courseInfo.id?.toString()
    );
    return completion?.completedAt ? new Date(completion.completedAt) : null;
  }, [courseInfo, batchInfo]);

  const maxDate = useMemo(() => {
    if (!completionDate) return null;
    // Set maxDate to the end of the completion month
    return new Date(completionDate.getFullYear(), completionDate.getMonth() + 1, 0);
  }, [completionDate]);

  const selectedDateKey = toLocalDateStr(date);
  const selectedDayData = monthlyData[selectedDateKey];

  const tileClassName = ({ date: tileDate, view }) => {
    if (view === 'month') {
      const dateKey = toLocalDateStr(tileDate);
      const data = monthlyData[dateKey];
      if (data) {
        let classes = '';
        if (data.status === 'present') classes += ' attendance-present';
        if (data.status === 'absent') classes += ' attendance-absent';
        // 'unmarked' days (holidays, or before a student joined) intentionally get no highlight
        // Note: 'completed' sessions are now mapped to 'present' in backend
        if (data.courseCompleted) classes += ' attendance-course-completed';
        return classes;
      }
    }
    return null;
  };

  const tileContent = ({ date: tileDate, view }) => {
    if (view === 'month') {
      const dateKey = toLocalDateStr(tileDate);
      const data = monthlyData[dateKey];
      if (data?.courseCompleted) {
        return (
          <div className="absolute top-0 right-0">
            <MdSchool className="text-amber-500 h-3 w-3" />
          </div>
        );
      }
    }
    return null;
  };

  const stats = useMemo(() => {
    const values = Object.values(monthlyData);
    return {
      present: values.filter(v => v.status === 'present').length,
      absent: values.filter(v => v.status === 'absent').length,
    };
  }, [monthlyData]);

  return (
    <div className="mt-3 flex h-full w-full flex-col font-dm md:gap-7 lg:flex-row">
      <div className="flex h-full w-full flex-col rounded-[20px] lg:w-[calc(100%-300px)] 3xl:w-[calc(100%-500px)]">
        <header className="relative mt-4 flex flex-col gap-1">
          <div className="text-xl font-bold text-navy-700 dark:text-white">
            Personal Attendance Dashboard
          </div>
          {courseInfo && (
            <div className="flex items-center gap-2 text-sm font-bold text-blue-500 bg-blue-50 dark:bg-blue-900/20 px-3 py-1 rounded-lg w-fit">
              <MdAssignment className="text-lg" />
              {courseInfo.name}
            </div>
          )}
        </header>

        {/* Stats Grid */}
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <Card extra="flex flex-col items-center p-4">
            <div className="flex items-center gap-2">
              <MdCheckCircle className="text-green-500 text-xl" />
              <p className="text-sm font-medium text-gray-600">Present Days</p>
            </div>
            <p className="text-2xl font-bold text-navy-700 dark:text-white">{stats.present}</p>
          </Card>
          <Card extra="flex flex-col items-center p-4">
            <div className="flex items-center gap-2">
              <MdCancel className="text-red-500 text-xl" />
              <p className="text-sm font-medium text-gray-600">Absent Days</p>
            </div>
            <p className="text-2xl font-bold text-navy-700 dark:text-white">{stats.absent}</p>
          </Card>
        </div>

        {/* Detail Section */}
        <div className="mt-6">
          <Card extra="p-6 h-full min-h-[400px]">
            <div className="flex items-center justify-between border-b pb-4 dark:border-white/10">
              <h3 className="text-lg font-bold text-navy-700 dark:text-white">
                Details for {date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </h3>
              {selectedDayData && (
                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
                  selectedDayData.status === 'present' ? 'bg-green-100 text-green-600' :
                  selectedDayData.status === 'absent' ? 'bg-red-100 text-red-600' :
                  'bg-gray-100 text-gray-500'
                }`}>
                  {selectedDayData.status}
                </span>
              )}
            </div>

            {!loading ? (
              <div className="mt-6 space-y-8">
                {selectedDayData?.courseCompleted && (
                  <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl">
                    <MdSchool className="text-amber-500 text-2xl" />
                    <div>
                      <p className="font-bold text-amber-700 dark:text-amber-400">Course Completed!</p>
                      <p className="text-sm text-amber-600 dark:text-amber-500">Congratulations on finishing your course on this date.</p>
                    </div>
                  </div>
                )}

                {selectedDayData ? (
                  <>
                    {/* Topics Section */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <MdOutlineTopic className="text-navy-700 dark:text-white text-xl" />
                        <h4 className="font-bold text-navy-700 dark:text-white">Topics Covered</h4>
                      </div>
                      {selectedDayData.topics?.length > 0 ? (
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-2">
                          {selectedDayData.topics.map((topic, i) => (
                            <li key={i} className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-navy-700 dark:bg-white shrink-0" />
                              <span className="text-sm">{topic}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-500 italic ml-2">No topics recorded for this session.</p>
                      )}
                    </div>

                    {/* Materials Section */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <MdOutlineLibraryBooks className="text-navy-700 dark:text-white text-xl" />
                        <h4 className="font-bold text-navy-700 dark:text-white">Learning Resources</h4>
                      </div>
                      {selectedDayData.materials?.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {selectedDayData.materials.map((material, i) => (
                            <div key={i} className="p-3 border rounded-xl dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                              <p className="text-sm font-bold text-navy-700 dark:text-white truncate">{material.title}</p>
                              {material.fileUrl || material.content ? (
                                <a 
                                  href="/student/view/all/training/materials" 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-500 font-bold hover:underline"
                                >
                                  View {material.type || 'Resource'}
                                </a>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic ml-2">No materials shared for this session.</p>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Nodatafound title="No activity recorded for this date." />
                    <p className="text-sm text-gray-500 mt-2">Try selecting a highlighted date on the calendar.</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy-700 dark:border-white" />
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Calendar Sidebar */}
      <div className="mt-1 flex h-full w-full flex-col items-center rounded-[20px] bg-white px-4 py-4 shadow-2xl shadow-gray-100 dark:!bg-navy-800 dark:shadow-none lg:w-[350px] 3xl:w-[470px]">
        <Card extra="p-4 w-full">
          <MiniCalendar 
            value={date} 
            onChange={handleDateChange} 
            width="100%" 
            tileClassName={tileClassName}
            tileContent={tileContent}
            onActiveStartDateChange={onActiveStartDateChange}
            maxDate={maxDate}
          />
        </Card>

        {/* Legend */}
        <Card extra="mt-4 p-4 w-full">
          <h4 className="text-sm font-bold text-navy-700 dark:text-white mb-3">Calendar Legend</h4>
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="h-3 w-3 rounded bg-green-500" />
              <span className="text-xs text-gray-600 dark:text-gray-400">Present</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-3 w-3 rounded bg-red-500" />
              <span className="text-xs text-gray-600 dark:text-gray-400">Absent</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-3 w-3 rounded border-2 border-amber-500" />
              <span className="text-xs text-gray-600 dark:text-gray-400">Course Completion Date</span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default StudentAttendanceReport;
